/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { IconType } from "react-icons/lib";
import { atom, RecoilState, useRecoilValue } from "recoil";
import StyledButton from "../../StyledButton";
import StyledCheckBox from "../../StyledCheckBox";

const ModalBody = styled.div`
  max-width: 400px;
  min-width: 400px;

  padding: 32px 0 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Icon = styled.img`
  min-width: 112px;

  margin-bottom: 32px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: center;
  color: #1a202c;
`;

const Description = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: #707070;
  text-align: center;
`;

const DescriptionCheck = styled.div`
  font-size: 12px;
  color: #979797;

  margin-left: 4px;
`;

const Buttons = styled.div`
  width: 100%;

  margin-top: 32px;

  display: flex;
  justify-content: center;
  align-items: center;
`;

interface DefaultProps {
  title: string;
  description: string | JSX.Element;
  descriptionCheck?: string | false;
  buttonsName: string[];
  onOkClicked: (checked: boolean) => void;
  onCancelClicked: (checked: boolean) => void;
  descriptionAsPre?: boolean;
  imageSize?: "sm" | "lg";
  IsLoadingAtom?: RecoilState<boolean>;
  isCampaign?: boolean;
  hasThirdButton?: boolean;
  labelThirdButton?: string;
  onClickThirdButton?: (checked: boolean) => void;
}

type Props =
  | (DefaultProps & {
      icon: string;
      fontAwesomeIcon?: IconType;
    })
  | (DefaultProps & {
      icon?: string;
      fontAwesomeIcon: () => JSX.Element;
    });

const DEFAULT_LoadingAtom = atom<boolean>({
  key: "WarningLoadingAtom",
  default: false,
});

export default function Warning(props: Props): JSX.Element {
  const {
    title,
    description,
    descriptionAsPre,
    descriptionCheck,
    buttonsName,
    onOkClicked,
    onCancelClicked,
    IsLoadingAtom,
    imageSize = "sm",
    isCampaign = false,
    hasThirdButton = false,
    labelThirdButton = "Label",
    onClickThirdButton = () => {},
  } = props;

  const [check, setCheck] = useState(false);
  const isLoadingFromAtom = useRecoilValue(
    IsLoadingAtom ? IsLoadingAtom : DEFAULT_LoadingAtom
  );

  if (isCampaign) {
    return (
      <ModalBody>
        {props.icon && (
          <Icon
            css={css`
              max-width: ${imageSize === "sm" ? "112px" : "224px"};
            `}
            src={props.icon}
          />
        )}
        {props.fontAwesomeIcon && <props.fontAwesomeIcon />}
        <Title>{title}</Title>
        {descriptionAsPre ? (
          <Description
            css={css`
              text-align: left;
            `}
            as="pre"
          >
            {description}
          </Description>
        ) : (
          <Description
            css={css`
              text-align: left;
            `}
          >
            {description}
          </Description>
        )}

        <Buttons>
          {descriptionCheck && (
            <>
              <StyledCheckBox
                check={check}
                handleCheck={() => {
                  setCheck((checked) => !checked);
                }}
              />
              <DescriptionCheck
                onClick={() => {
                  setCheck((checked) => !checked);
                }}
              >
                {descriptionCheck}
              </DescriptionCheck>
            </>
          )}
        </Buttons>

        {buttonsName[0] && (
          <>
            <Buttons>
              <StyledButton
                inverted
                css={css`
                  min-width: 100px;
                  margin-right: 12px;
                `}
                onClick={() => {
                  onCancelClicked(check);
                }}
              >
                {buttonsName[0]}
              </StyledButton>

              <StyledButton
                css={css`
                  min-width: 100px;
                `}
                disable={!check}
                isLoading={isLoadingFromAtom}
                onClick={() => {
                  onOkClicked(check);
                }}
              >
                {buttonsName[1]}
              </StyledButton>
            </Buttons>
          </>
        )}
      </ModalBody>
    );
  }

  return (
    <ModalBody>
      {props.icon && (
        <Icon
          css={css`
            max-width: ${imageSize === "sm" ? "112px" : "224px"};
          `}
          src={props.icon}
        />
      )}
      {props.fontAwesomeIcon && <props.fontAwesomeIcon />}
      <Title>{title}</Title>
      {descriptionAsPre ? (
        <Description as="pre">{description}</Description>
      ) : (
        <Description>{description}</Description>
      )}

      {buttonsName.length > 1 ? (
        buttonsName[0] && (
          <>
            <Buttons>
              {hasThirdButton && (
                <StyledButton
                  inverted
                  css={css`
                    min-width: 100px;
                    margin-right: 12px;
                  `}
                  onClick={() => {
                    onClickThirdButton(check);
                  }}
                >
                  {labelThirdButton}
                </StyledButton>
              )}

              <StyledButton
                inverted
                css={css`
                  min-width: 100px;
                  margin-right: 12px;
                `}
                onClick={() => {
                  onCancelClicked(check);
                }}
              >
                {buttonsName[0]}
              </StyledButton>

              <StyledButton
                css={css`
                  min-width: 100px;
                `}
                isLoading={isLoadingFromAtom}
                onClick={() => {
                  onOkClicked(check);
                }}
              >
                {buttonsName[1]}
              </StyledButton>
            </Buttons>
          </>
        )
      ) : (
        <Buttons>
          <StyledButton
            css={css`
              min-width: 100px;
            `}
            isLoading={isLoadingFromAtom}
            onClick={() => {
              onOkClicked(check);
            }}
          >
            {buttonsName[0]}
          </StyledButton>
        </Buttons>
      )}
      <Buttons>
        {descriptionCheck && (
          <>
            <StyledCheckBox
              check={check}
              handleCheck={() => {
                setCheck((checked) => !checked);
              }}
            />
            <DescriptionCheck
              onClick={() => {
                setCheck((checked) => !checked);
              }}
            >
              {descriptionCheck}
            </DescriptionCheck>
          </>
        )}
      </Buttons>
    </ModalBody>
  );
}
